import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";
import AuthService from "../../../api/services/AuthService";
import TradePairsSection from "../TradePage/components/TradePairsSection";
import OtcCoinDropdown from "./OtcCoinDropdown";
import "../TradePage/trade_new.css";

function positiveLast(val) {
  if (val == null || val === "") return null;
  const n = Number(String(val).replace(/,/g, ""));
  return Number.isFinite(n) && n > 0 ? String(n) : null;
}

function formatAvail(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "0";
  return n.toLocaleString(undefined, { maximumFractionDigits: 8 });
}

function amountPlaces(code) {
  const asset = String(code || "").toUpperCase();
  if (asset === "USDT" || asset === "USDC" || asset === "USD") return 2;
  return 8;
}

function hasPositiveAmount(value) {
  const text = String(value || "").trim();
  if (!/^\d+(\.\d+)?$/.test(text)) return false;
  return Number(text) > 0;
}

/** Digits and one decimal point only. Letters such as "0.0d" are dropped. */
function sanitizeAmount(raw, places = 8) {
  let text = String(raw ?? "").replace(/[^\d.]/g, "");
  const firstDot = text.indexOf(".");
  if (firstDot >= 0) {
    text = text.slice(0, firstDot + 1) + text.slice(firstDot + 1).replace(/\./g, "");
  }
  const parts = text.split(".");
  const whole = (parts[0] || "").replace(/^0+(?=\d)/, "");
  if (parts.length === 1) return whole;
  return `${whole || "0"}.${parts[1].slice(0, places)}`;
}

const OtcRfqForm = ({
  quoteType,
  onQuoteTypeChange,
  buyOpen = true,
  sellOpen = true,
  pairKey,
  onPairChange,
  pairOptions = [],
  spotPairs = [],
  baseOptions = [],
  amount,
  onAmountChange,
  onAmountAssetChange,
  onMax,
  minBase,
  amountApprox,
  minQuoteLabel,
  amountError,
  amountWarning,
  available,
  availableAsset,
  lockedBalance,
  amountAsset,
  assetsLoading,
  submitting,
  deskOpen = true,
  isLoggedIn,
  loginHref,
  ticker,
  onSubmit,
  passThrough = false,
}) => {
  const [pairBase = "BTC", pairQuote = "USDT"] = String(pairKey || "BTC/USDT").split("/");
  const [pairMenuOpen, setPairMenuOpen] = useState(false);
  const [pairSearch, setPairSearch] = useState("");
  const amountRef = useRef(null);
  const [pairFilter, setPairFilter] = useState("USDT");
  const [favCoins, setFavCoins] = useState([]);
  const pairMenuRef = useRef(null);
  const token = AuthService.getToken?.() || "";
  const isBuy = quoteType === "buy";
  const coin = amountAsset || pairBase;
  const places = amountPlaces(coin);
  const amountValue = sanitizeAmount(amount, places);
  const amountReady = hasPositiveAmount(amountValue) && !amountError;

  const lastPrice = positiveLast(ticker?.last);
  const rateNum = lastPrice ? Number(lastPrice) : 0;
  const formattedRate = rateNum > 0
    ? rateNum.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    : "";
  const changeN = Number(String(ticker?.change_percent ?? "").replace(/,/g, ""));
  const changeText = Number.isFinite(changeN) && formattedRate
    ? `${changeN > 0 ? "+" : ""}${changeN.toFixed(2)}%`
    : "";

  const pairRows = useMemo(() => {
    const live = new Map();
    (Array.isArray(spotPairs) ? spotPairs : []).forEach((row) => {
      const base = String(row?.base_currency || "").toUpperCase();
      const quote = String(row?.quote_currency || "").toUpperCase();
      if (base && quote) live.set(`${base}/${quote}`, row);
    });
    const query = pairSearch.trim().toLowerCase();
    return pairOptions
      .map((opt) => {
        const symbol = String(opt.symbol || "").toUpperCase();
        const [base, quote] = symbol.split("/");
        const row = live.get(symbol);
        return {
          _id: row?._id,
          base_currency: base,
          quote_currency: quote,
          base_currency_fullname: opt.name || row?.base_currency_fullname || base,
          icon_path: opt.iconPath || row?.icon_path || "",
          buy_price: row?.buy_price || row?.sell_price || "",
          change_percentage: Number(row?.change_percentage ?? row?.change ?? 0),
          change: row?.change,
        };
      })
      .filter((row) => {
        if (!row.base_currency || !row.quote_currency) return false;
        if (!query) return true;
        const name = String(row.base_currency_fullname || "").toLowerCase();
        return `${row.base_currency}/${row.quote_currency}`.toLowerCase().includes(query) || name.includes(query);
      });
  }, [pairOptions, spotPairs, pairSearch]);

  const selectedPairRow = pairRows.find(
    (row) => `${row.base_currency}/${row.quote_currency}` === String(pairKey || "").toUpperCase()
  ) || {
    base_currency: pairBase,
    quote_currency: pairQuote,
    base_currency_fullname: pairBase,
  };

  const coinIconSrc = (path) => {
    const s = String(path || "");
    if (!s) return "/images/icon/btc.png";
    if (s.startsWith("http") || s.startsWith("/images/")) return s;
    return resolvePublicUploadUrl(s.replace(/^\//, ""));
  };

  useEffect(() => {
    if (!pairMenuOpen) return undefined;
    const close = (event) => {
      if (pairMenuRef.current && !pairMenuRef.current.contains(event.target)) {
        setPairMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [pairMenuOpen]);

  useEffect(() => {
    const clean = sanitizeAmount(amount, places);
    if (clean !== String(amount ?? "") && typeof onAmountChange === "function") {
      onAmountChange(clean);
    }
  }, [amount, onAmountChange, places]);

  const handleAmountChange = (e) => {
    const clean = sanitizeAmount(e.target.value, places);
    if (clean === amountValue) {
      e.target.value = clean;
      return;
    }
    onAmountChange(clean);
  };

  return (
    <div className="otc-rfq-form-container">
      <h3 className="otc-card-title">Request Quote</h3>



      <div className={`quote-tabs-container ${isBuy ? "is-buy" : "is-sell"}`}>
        <button
          type="button"
          className={`quote-tab-pill buy ${isBuy ? "active" : ""}`}
          onClick={() => onQuoteTypeChange("buy")}
          disabled={!buyOpen}
        >
          Buy
        </button>
        <button
          type="button"
          className={`quote-tab-pill sell ${!isBuy ? "active" : ""}`}
          onClick={() => onQuoteTypeChange("sell")}
          disabled={!sellOpen}
        >
          Sell
        </button>
      </div>

      <div className="quote-pair-price-row quote-input-card ">
        <div className="otc-pair-picker " ref={pairMenuRef}>
          <button
            type="button"
            className="otc-landing-pair-btn"
            aria-expanded={pairMenuOpen}
            disabled={assetsLoading || !pairOptions.length}
            onClick={() => setPairMenuOpen((open) => !open)}
          >
            <span>{pairKey || "BTC/USDT"}</span>
            <i className={`ri-arrow-${pairMenuOpen ? "up" : "down"}-s-line`} />
          </button>
          <TradePairsSection
            setIsFavouritesOpen={setPairMenuOpen}
            isFavouritesOpen={pairMenuOpen}
            closeOnLeave={false}
            token={token}
            coinFilter={pairFilter}
            setcoinFilter={setPairFilter}
            CoinPairDetails={pairRows}
            search={pairSearch}
            setsearch={setPairSearch}
            favCoins={favCoins}
            SelectedCoin={selectedPairRow}
            handleSelectCoin={(coin) => {
              const symbol = `${coin?.base_currency || ""}/${coin?.quote_currency || ""}`.toUpperCase();
              if (symbol !== "/" && typeof onPairChange === "function") onPairChange(symbol);
              setPairMenuOpen(false);
            }}
            coinIconSrc={coinIconSrc}
            handleAddFav={(coinId) => {
              setFavCoins((prev) => (
                prev.includes(coinId) ? prev.filter((id) => id !== coinId) : [...prev, coinId]
              ));
              if (token) AuthService.addFavorite?.(coinId).catch(() => { });
            }}
            hideQuoteTabs
          />
        </div>
        {formattedRate ? (
          <div className="quote-live-price">
            <span className="quote-live-last">{formattedRate}</span>
            {changeText ? (
              <span className={`quote-live-change ${changeN >= 0 ? "is-up" : "is-down"}`}>
                {changeText}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="quote-field-group">
        <div className="quote-field-header">
          <label className="quote-field-label">Amount</label>
          {isLoggedIn && available !== "" && (
            <div className="quote-avail-wrap">
              <span className="quote-avail-text otc-balance-tip">
                Available: {formatAvail(available)} {availableAsset || (isBuy ? pairQuote : pairBase)}
                {Number(lockedBalance) > 0
                  ? ` · Locked: ${formatAvail(lockedBalance)}`
                  : ""}
                <span className="otc-balance-tip__icon">
                  <i className="ri-information-line" aria-hidden="true" />
                  <span className="otc-balance-tip__text" role="tooltip">
                    This is the available balance in your Spot wallet. OTC uses the same balance.
                  </span>
                </span>
              </span>
            </div>
          )}
        </div>
        <div
          className="quote-input-card quote-amount-card"
          onMouseDown={(event) => {
            if (event.target.closest(".quote-amount-side")) return;
            if (event.target === amountRef.current) return;
            event.preventDefault();
            amountRef.current?.focus();
          }}
        >
          <input
            ref={amountRef}
            type="text"
            inputMode="decimal"
            placeholder={minBase || "0"}
            className="quote-field-input quote-amount-input"
            value={amountValue}
            onChange={handleAmountChange}
            autoComplete="off"
          />
          <div className="quote-amount-side">
            <OtcCoinDropdown
              value={coin}
              onChange={(symbol) => {
                if (typeof onAmountAssetChange === "function") onAmountAssetChange(symbol);
              }}
              options={baseOptions.length ? baseOptions : [
                { symbol: pairBase, name: pairBase, iconPath: "" },
                { symbol: pairQuote, name: pairQuote, iconPath: "" },
              ]}
              variant="coin"
              align="end"
            />
            {typeof onMax === "function" && (
              <button type="button" className="otc-inline-max-btn" onClick={onMax}>
                Max
              </button>
            )}
          </div>
        </div>
        {minQuoteLabel ? <p className="quote-amount-approx">{minQuoteLabel}</p> : null}
        {amountApprox ? <p className="quote-amount-approx">{amountApprox}</p> : null}
        {amountError ? <p className="quote-field-error">{amountError}</p> : null}
        {!amountError && amountWarning ? <p className="quote-field-warning">{amountWarning}</p> : null}
      </div>

      {isLoggedIn && !deskOpen ? (
        <p className="otc-desk-closed">The OTC desk is closed. New requests open again during desk hours.</p>
      ) : null}

      {!isLoggedIn && !passThrough ? (
        <Link
          to={loginHref || "/login"}
          state={{ redirectTo: "/otc/rfq" }}
          className="btn-request-quote"
        >
          Request Quote
        </Link>
      ) : (
        <button
          type="button"
          className="btn-request-quote"
          onClick={onSubmit}
          disabled={
            passThrough
              ? assetsLoading || !amountReady
              : (
                submitting
                || assetsLoading
                || !deskOpen
                || (isBuy ? !buyOpen : !sellOpen)
                || !amountReady
              )
          }
        >
          {passThrough
            ? "Continue"
            : (!deskOpen ? "Desk closed" : submitting ? "Requesting Quote…" : "Request Quote")}
        </button>
      )}
    </div>
  );
};

export default OtcRfqForm;
